import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { CheckCircle2, Clock, RefreshCw, XCircle, ArrowRight } from "lucide-react";
import Layout from "../components/Layout";
import { useLocale } from "../context/LocaleContext";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const POLL_MS = 2500;
const MAX_POLLS = 24;

export default function PaymentResult() {
  const { t } = useTranslation();
  const { formatAmount } = useLocale();
  const [params] = useSearchParams();
  const orderId = params.get("order_id");
  const [order, setOrder] = useState(null);
  const [state, setState] = useState(orderId ? "verifying" : "missing");
  const polls = useRef(0);

  useEffect(() => {
    if (!orderId) return;
    let timer;
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      try {
        const res = await axios.get(`${API}/billing/orders/${orderId}`, { withCredentials: true });
        if (cancelled) return;
        setOrder(res.data);
        if (res.data.state === "paid") { setState("paid"); return; }
        if (res.data.state === "failed") { setState("failed"); return; }
        polls.current += 1;
        if (polls.current >= MAX_POLLS) { setState("pending"); return; }
        timer = setTimeout(poll, POLL_MS);
      } catch (err) {
        if (cancelled) return;
        if (err.response?.status === 404) { setState("missing"); return; }
        polls.current += 1;
        if (polls.current >= MAX_POLLS) { setState("pending"); return; }
        timer = setTimeout(poll, POLL_MS);
      }
    };
    poll();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [orderId]);

  const planLabel = order?.plan_id ? t(`billing.plans.labels.${order.plan_id}`, { defaultValue: order.plan_id }) : "";
  const isSub = order?.kind === "subscription" || order?.kind === "renewal";

  const views = {
    verifying: { icon: RefreshCw, color: "#c084fc", spin: true, title: t("billing.result.verifying"), body: t("billing.result.verifyingBody") },
    paid: {
      icon: CheckCircle2, color: "#86efac", title: t("billing.result.successTitle"),
      body: isSub
        ? t("billing.result.successSubscription", { plan: planLabel, credits: order?.credits })
        : t("billing.result.successTopup", { credits: order?.credits }),
    },
    pending: { icon: Clock, color: "#fbbf24", title: t("billing.result.pendingTitle"), body: t("billing.result.pendingBody") },
    failed: { icon: XCircle, color: "#f87171", title: t("billing.result.failedTitle"), body: t("billing.result.failedBody") },
    missing: { icon: XCircle, color: "#f87171", title: t("billing.result.missing"), body: "" },
  };
  const v = views[state];
  const Icon = v.icon;

  return (
    <Layout>
      <div className="max-w-lg mx-auto px-4 py-16" data-testid="payment-result-page">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          className="rounded-3xl p-8 text-center" style={{ background: "#161616", border: "1px solid #252525" }}>
          <div className="w-16 h-16 rounded-2xl mx-auto mb-5 flex items-center justify-center" style={{ background: `${v.color}14` }}>
            <Icon className={`w-8 h-8 ${v.spin ? "animate-spin" : ""}`} style={{ color: v.color }} />
          </div>
          <h1 className="text-2xl font-black text-white mb-2" data-testid={`payment-result-${state}`}>{v.title}</h1>
          {v.body && <p className="text-sm leading-relaxed" style={{ color: "#a0a0a0" }}>{v.body}</p>}
          {state === "failed" && order?.failure_reason && (
            <p className="text-xs mt-3" style={{ color: "#6b6b6b" }} data-testid="payment-failure-reason">
              {t("billing.result.reason", { reason: order.failure_reason })}
            </p>
          )}
          {order && state !== "missing" && (
            <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold"
              style={{ background: "#0e0e0e", border: "1px solid #2a2a2a", color: "#c084fc" }} data-testid="payment-order-summary">
              {isSub ? planLabel : t("common.creditsCount", { count: order.credits })}
              <span style={{ color: "#555" }}>·</span>
              <span dir="ltr">{formatAmount(order.amount, order.currency)}</span>
            </div>
          )}
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            {state === "paid" ? (
              <>
                <Link to="/projects/new" data-testid="payment-result-create-ad"
                  className="px-5 py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2"
                  style={{ background: "#7c3aed", color: "#fff" }}>
                  {t("billing.result.createAd")} <ArrowRight className="w-4 h-4 rtl:rotate-180" />
                </Link>
                <Link to="/dashboard" data-testid="payment-result-dashboard"
                  className="px-5 py-3 rounded-xl text-sm font-bold" style={{ background: "#252525", color: "#ddd" }}>
                  {t("billing.result.goDashboard")}
                </Link>
              </>
            ) : (
              <Link to="/billing" data-testid="payment-result-back-billing"
                className="px-5 py-3 rounded-xl text-sm font-bold" style={{ background: "#252525", color: "#ddd" }}>
                {t("billing.result.backToBilling")}
              </Link>
            )}
          </div>
        </motion.div>
      </div>
    </Layout>
  );
}
